import React, { useState, useEffect } from 'react';
import { X, Sparkles, Phone, ArrowRight } from 'lucide-react';
import { companyInfo } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

const SpecialOfferPopup = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Check if user has already seen the popup in this session
    const hasSeenOffer = sessionStorage.getItem('hasSeenOffer');
    
    if (!hasSeenOffer) {
      // Show popup after 30 seconds
      const timer = setTimeout(() => {
        setIsVisible(true);
        sessionStorage.setItem('hasSeenOffer', 'true');
      }, 30000); // 30 seconds

      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  const handleContact = () => {
    handleClose();
    // Scroll to contact section
    const element = document.getElementById('contacto');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCall = () => {
    window.location.href = `tel:${companyInfo.phone}`;
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Popup Content - Compact for mobile */}
      <div 
        className={`relative bg-white rounded-2xl md:rounded-3xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden transform transition-all duration-300 ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
      >
        {/* Close Button - Responsive size */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 md:top-3 md:right-3 z-20 w-10 h-10 md:w-12 md:h-12 bg-slate-900 hover:bg-slate-800 rounded-full flex items-center justify-center transition-colors shadow-lg"
          aria-label="Close"
        >
          <X className="w-5 h-5 md:w-6 md:h-6 text-white" strokeWidth={3} />
        </button>

        {/* Header with gradient background - Compact */}
        <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-blue-500 p-4 md:p-8 text-white overflow-hidden">
          {/* Animated circles */}
          <div className="absolute top-0 right-0 w-24 md:w-32 h-24 md:h-32 bg-white/10 rounded-full blur-2xl animate-pulse" />
          <div className="absolute bottom-0 left-0 w-32 md:w-40 h-32 md:h-40 bg-white/10 rounded-full blur-3xl animate-pulse delay-1000" />
          
          <div className="relative z-10">
            {/* Badge - Smaller on mobile */}
            <div className="inline-flex items-center gap-1.5 md:gap-2 bg-white/20 backdrop-blur-sm px-2.5 md:px-4 py-1 md:py-2 rounded-full mb-2 md:mb-4">
              <Sparkles className="w-3 h-3 md:w-4 md:h-4" />
              <span className="text-xs md:text-sm font-bold">{t('popup.badge')}</span>
            </div>

            {/* Main Offer - Responsive text */}
            <h2 className="text-3xl md:text-5xl font-bold mb-1 md:mb-2">
              {t('popup.discount')}
            </h2>
            <p className="text-sm md:text-xl font-semibold mb-0.5 md:mb-1">
              {t('popup.subtitle')}
            </p>
            <p className="text-blue-100 text-xs md:text-sm">
              {t('popup.limited')}
            </p>
          </div>
        </div>

        {/* Content - Compact padding */}
        <div className="p-4 md:p-8">
          <h3 className="text-base md:text-2xl font-bold text-slate-900 mb-2 md:mb-4">
            {t('popup.title')}
          </h3>
          
          {/* Services list - Compact */}
          <div className="space-y-1.5 md:space-y-3 mb-3 md:mb-6">
            <div className="flex items-center gap-2 md:gap-3 text-slate-600 text-xs md:text-base">
              <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-full flex-shrink-0" />
              <span>{t('popup.service1')}</span>
            </div>
            <div className="flex items-center gap-2 md:gap-3 text-slate-600 text-xs md:text-base">
              <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-full flex-shrink-0" />
              <span>{t('popup.service2')}</span>
            </div>
            <div className="flex items-center gap-2 md:gap-3 text-slate-600 text-xs md:text-base">
              <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-blue-500 rounded-full flex-shrink-0" />
              <span>{t('popup.service3')}</span>
            </div>
          </div>

          {/* Trust indicators - Compact */}
          <div className="bg-blue-50 rounded-xl md:rounded-2xl p-2.5 md:p-4 mb-3 md:mb-6">
            <div className="flex items-center justify-around text-center">
              <div>
                <p className="text-lg md:text-2xl font-bold text-blue-600">15+</p>
                <p className="text-[10px] md:text-xs text-slate-600">{t('popup.years')}</p>
              </div>
              <div className="w-px h-6 md:h-10 bg-blue-200" />
              <div>
                <p className="text-lg md:text-2xl font-bold text-blue-600">500+</p>
                <p className="text-[10px] md:text-xs text-slate-600">{t('popup.projects')}</p>
              </div>
              <div className="w-px h-6 md:h-10 bg-blue-200" />
              <div>
                <p className="text-lg md:text-2xl font-bold text-blue-600">100%</p>
                <p className="text-[10px] md:text-xs text-slate-600">{t('popup.satisfaction')}</p>
              </div>
            </div>
          </div>

          {/* CTA Buttons - Compact */}
          <div className="space-y-2 md:space-y-3">
            <button
              onClick={handleContact}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-2.5 md:py-4 rounded-lg md:rounded-xl text-xs md:text-base font-bold hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300 flex items-center justify-center gap-1.5 md:gap-2 group"
            >
              <span className="text-center">{t('popup.cta')}</span>
              <ArrowRight className="w-3.5 h-3.5 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform flex-shrink-0" />
            </button>

            <button
              onClick={handleCall}
              className="w-full bg-slate-900 text-white py-2.5 md:py-4 rounded-lg md:rounded-xl text-xs md:text-base font-bold hover:bg-slate-800 transition-all duration-300 flex items-center justify-center gap-1.5 md:gap-2"
            >
              <Phone className="w-3.5 h-3.5 md:w-5 md:h-5 flex-shrink-0" />
              <span>{t('popup.call')}: {companyInfo.phone}</span>
            </button>
          </div>

          {/* Fine print - Smaller */}
          <p className="text-[9px] md:text-xs text-slate-400 text-center mt-2 md:mt-4 leading-tight">
            {t('popup.disclaimer')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SpecialOfferPopup;
